import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildWorkspaceTabUrl,
  getAllWorkspaceTabs,
  resolveActiveWorkspaceTabKey,
} from '../config/workspaceTabs';

const DEFAULT_TAB = '/dashboard';
const OPEN_TABS_STORAGE_KEY = 'qms.workspace.openTabs';

function loadPersistedOpenTabs() {
  try {
    const raw = sessionStorage.getItem(OPEN_TABS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return new Set(parsed);
      }
    }
  } catch {
    /* ignore */
  }
  return new Set([DEFAULT_TAB]);
}

const WorkspaceTabsContext = createContext(null);

export function WorkspaceTabsProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const allTabs = useMemo(() => getAllWorkspaceTabs(), []);

  const [openTabs, setOpenTabs] = useState(loadPersistedOpenTabs);

  const activeTabKey =
    resolveActiveWorkspaceTabKey(location, allTabs) || location.pathname;

  useEffect(() => {
    try {
      sessionStorage.setItem(
        OPEN_TABS_STORAGE_KEY,
        JSON.stringify(Array.from(openTabs))
      );
    } catch {
      /* ignore */
    }
  }, [openTabs]);

  useEffect(() => {
    const tabKey = resolveActiveWorkspaceTabKey(location, allTabs);
    if (tabKey) {
      setOpenTabs((prev) => new Set([...prev, tabKey]));
    }
  }, [location.pathname, location.search, allTabs]);

  const openWorkspaceTab = useCallback(
    (tabKey) => {
      if (!allTabs[tabKey]) {
        return;
      }
      setOpenTabs((prev) => new Set([...prev, tabKey]));
      navigate(buildWorkspaceTabUrl(tabKey, location));
    },
    [allTabs, location, navigate]
  );

  const closeTab = useCallback(
    (tabKey, event) => {
      event?.stopPropagation();
      if (openTabs.size <= 1) return;

      const remainingTabs = Array.from(openTabs).filter((k) => k !== tabKey);

      if (tabKey === activeTabKey && remainingTabs.length > 0) {
        const nextKey = remainingTabs[remainingTabs.length - 1];
        navigate(buildWorkspaceTabUrl(nextKey, location));
      }

      setOpenTabs((prev) => {
        const next = new Set(prev);
        next.delete(tabKey);
        return next;
      });
    },
    [activeTabKey, location, navigate, openTabs]
  );

  const isTabActive = useCallback(
    (tabKey) => activeTabKey === tabKey,
    [activeTabKey]
  );

  const navigateToTab = useCallback(
    (tabKey) => {
      if (!allTabs[tabKey]) return;
      setOpenTabs((prev) => new Set([...prev, tabKey]));
      navigate(buildWorkspaceTabUrl(tabKey, location));
    },
    [allTabs, location, navigate]
  );

  const value = useMemo(
    () => ({
      allTabs,
      openTabs,
      activeTabKey,
      openWorkspaceTab,
      closeTab,
      isTabActive,
      navigateToTab,
    }),
    [
      allTabs,
      openTabs,
      activeTabKey,
      openWorkspaceTab,
      closeTab,
      isTabActive,
      navigateToTab,
    ]
  );

  return (
    <WorkspaceTabsContext.Provider value={value}>
      {children}
    </WorkspaceTabsContext.Provider>
  );
}

export function useWorkspaceTabs() {
  const context = useContext(WorkspaceTabsContext);
  if (!context) {
    throw new Error('useWorkspaceTabs must be used within WorkspaceTabsProvider');
  }
  return context;
}

/** Safe for Sidebar — returns null when workspace tabs are disabled (non-Electron). */
export function useWorkspaceTabsOptional() {
  return useContext(WorkspaceTabsContext);
}
